import { devLog } from '@/lib/utils';

// ─────────────────────────────────────────────────────────────────────────────
//  Camera + MediaRecorder wrapper for live video answers.
//
//  Unlike lib/audio-config.ts (which shares module-level singletons with the
//  playback system), each createVideoRecorder() call owns its own stream and
//  recorder so opening the camera can never disturb interview audio playback.
//  Lifecycle: openCamera() → beginRecording() → stopRecording() → destroy().
// ─────────────────────────────────────────────────────────────────────────────

export type CameraFacing = 'user' | 'environment';

/** Preference order matters: Safari (iOS) only records mp4; Chrome/Firefox
 *  take the webm variants. The first supported type wins. */
const MIME_CANDIDATES = [
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

/** Same chain lib/audio-config.ts uses for voice notes. */
const AUDIO_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

/** Capture target. Interview answers are cut into broadcast productions
 *  downstream (eProducer news shows), so the take is captured at full HD:
 *  the browser is asked for 1920×1080 (`ideal`, so a camera that can't do it
 *  still opens at its best mode) and encoded at a bitrate that keeps 1080p
 *  detail instead of the 1.8Mbps/720p "keep it small" settings this used to
 *  ship with. The video no longer has to fit the 50MB transcribe cap: the
 *  raw take goes to S3 through a presigned PUT (500MB cap) and only the
 *  parallel audio track is transcribed. */
export const CAPTURE_WIDTH = 1920;
export const CAPTURE_HEIGHT = 1080;
export const CAPTURE_FRAME_RATE = 30;
export const MAX_RECORDING_MS = 3 * 60 * 1000;
/** ≈6Mbps: 1080p30 talking-head quality on VP9/H.264 (a 3-minute take is
 *  ~140MB, well inside the presigned-upload cap). */
const VIDEO_BITS_PER_SECOND = 6_000_000;
const AUDIO_BITS_PER_SECOND = 128_000;

/** Byte-based twin of the duration cap. `videoBitsPerSecond` is only a hint —
 *  some browsers (notably iOS Safari) record at whatever bitrate they like —
 *  so the recorder also watches actual accumulated bytes and asks to stop at
 *  this threshold, guaranteeing the take stays under the presigned-upload
 *  cap (500MB) with room to spare no matter what bitrate the browser really
 *  used. iOS Safari records 1080p H.264 at roughly 8–12Mbps, so a full
 *  3-minute take lands around 200–270MB. */
export const MAX_RECORDING_BYTES = 300 * 1024 * 1024;

export interface VideoRecordingResult {
  videoBlob: Blob;
  /** The same take's audio track, captured by a parallel audio-only
   *  MediaRecorder on the same stream. Null when the browser couldn't run a
   *  second recorder — callers fall back to server-side audio extraction. */
  audioBlob: Blob | null;
}

export interface VideoRecorder {
  /** Open the camera + mic and return the live preview stream. Safe to call
   *  again with a different facing mode (tears down the previous stream). */
  openCamera(facing?: CameraFacing): Promise<MediaStream>;
  /** Start capturing. Requires an open camera. `onSizeLimit` fires once if the
   *  take reaches MAX_RECORDING_BYTES before the duration cap — the caller
   *  should stop the recording, exactly as it does for the timer. */
  beginRecording(onSizeLimit?: () => void): void;
  /** Stop capturing and resolve with the recorded blobs. */
  stopRecording(): Promise<VideoRecordingResult>;
  isRecording(): boolean;
  /** The MIME type the recorder settled on ('' until beginRecording). */
  getMimeType(): string;
  /** Release camera/mic tracks and drop all recorder state. */
  destroy(): void;
}

export function pickSupportedMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const candidate of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(candidate)) return candidate;
    } catch {
      // isTypeSupported can throw on exotic UAs — just try the next one.
    }
  }
  return '';
}

function pickSupportedAudioMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const candidate of AUDIO_MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(candidate)) return candidate;
    } catch {
      // Try the next one.
    }
  }
  return '';
}

/** Filename whose extension matches what the blob actually contains, so the
 *  server's extension-based handling (and S3 keys) stay truthful. */
export function videoBlobFilename(blob: Blob): string {
  const type = blob.type || '';
  if (type.includes('mp4')) return 'video-answer.mp4';
  if (type.includes('quicktime')) return 'video-answer.mov';
  return 'video-answer.webm';
}

export function isVideoCaptureSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined' &&
    pickSupportedMimeType() !== ''
  );
}

export function createVideoRecorder(): VideoRecorder {
  let stream: MediaStream | null = null;
  let recorder: MediaRecorder | null = null;
  let audioRecorder: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let audioChunks: Blob[] = [];
  let mimeType = '';
  let audioMimeType = '';
  let recording = false;

  const stopTracks = () => {
    stream?.getTracks().forEach(track => {
      try {
        track.stop();
      } catch {
        // Track already stopped.
      }
    });
    stream = null;
  };

  /** Stop the parallel audio recorder and resolve with its blob (null if the
   *  audio side never started or produced nothing usable). */
  const stopAudioRecorder = (): Promise<Blob | null> => {
    return new Promise(resolve => {
      const active = audioRecorder;
      audioRecorder = null;
      if (!active || active.state === 'inactive') {
        resolve(null);
        return;
      }
      // Never let a wedged audio recorder hold up the video result.
      const timeout = setTimeout(() => resolve(null), 3000);
      active.onstop = () => {
        clearTimeout(timeout);
        const type = audioMimeType.split(';')[0] || 'audio/webm';
        const blob = new Blob(audioChunks, { type });
        audioChunks = [];
        resolve(blob.size > 0 ? blob : null);
      };
      active.onerror = () => {
        clearTimeout(timeout);
        resolve(null);
      };
      try {
        active.stop();
      } catch {
        clearTimeout(timeout);
        resolve(null);
      }
    });
  };

  return {
    async openCamera(facing: CameraFacing = 'user'): Promise<MediaStream> {
      stopTracks();
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facing,
          width: { ideal: CAPTURE_WIDTH },
          height: { ideal: CAPTURE_HEIGHT },
          frameRate: { ideal: CAPTURE_FRAME_RATE },
        },
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      const settings = stream.getVideoTracks()[0]?.getSettings?.();
      devLog.log(
        `[videoRecorder] camera open (${facing}) ${settings?.width ?? '?'}x${settings?.height ?? '?'}@${settings?.frameRate ?? '?'}`,
      );
      return stream;
    },

    beginRecording(onSizeLimit?: () => void): void {
      if (!stream) throw new Error('Camera is not open');
      if (recording) return;
      chunks = [];
      mimeType = pickSupportedMimeType();
      recorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
      let recordedBytes = 0;
      let sizeLimitFired = false;
      recorder.ondataavailable = event => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
          recordedBytes += event.data.size;
          if (!sizeLimitFired && recordedBytes >= MAX_RECORDING_BYTES && onSizeLimit) {
            sizeLimitFired = true;
            devLog.warn(
              `[videoRecorder] size limit reached at ${recordedBytes} bytes — requesting stop`,
            );
            onSizeLimit();
          }
        }
      };
      // Timeslice so long takes flush incrementally instead of buffering the
      // whole recording into one giant in-memory chunk.
      recorder.start(1000);
      recording = true;
      devLog.log(`[videoRecorder] recording started (${mimeType || 'default mime'})`);

      // Parallel audio-only capture of the same take. The tiny audio blob is
      // what gets transcribed (fast), so the reply latency doesn't depend on
      // the video's size. Best-effort: if a second recorder can't run here,
      // callers fall back to server-side audio extraction from the video.
      try {
        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length > 0) {
          audioChunks = [];
          audioMimeType = pickSupportedAudioMimeType();
          audioRecorder = new MediaRecorder(new MediaStream(audioTracks), {
            ...(audioMimeType ? { mimeType: audioMimeType } : {}),
            audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
          });
          audioRecorder.ondataavailable = event => {
            if (event.data && event.data.size > 0) audioChunks.push(event.data);
          };
          audioRecorder.start(1000);
          devLog.log(`[videoRecorder] parallel audio started (${audioMimeType || 'default mime'})`);
        }
      } catch (error: any) {
        devLog.warn(
          '[videoRecorder] parallel audio recorder unavailable:',
          error?.message || error,
        );
        audioRecorder = null;
      }
    },

    stopRecording(): Promise<VideoRecordingResult> {
      return new Promise((resolve, reject) => {
        if (!recorder || !recording) {
          reject(new Error('Not recording'));
          return;
        }
        const audioPromise = stopAudioRecorder();
        const activeRecorder = recorder;
        activeRecorder.onstop = async () => {
          recording = false;
          const type = mimeType.split(';')[0] || 'video/webm';
          const videoBlob = new Blob(chunks, { type });
          chunks = [];
          const audioBlob = await audioPromise;
          devLog.log(
            `[videoRecorder] recording stopped: video ${videoBlob.size} bytes (${videoBlob.type}), ` +
              `audio ${audioBlob ? `${audioBlob.size} bytes (${audioBlob.type})` : 'unavailable'}`,
          );
          resolve({ videoBlob, audioBlob });
        };
        activeRecorder.onerror = event => {
          recording = false;
          reject((event as any)?.error || new Error('MediaRecorder error'));
        };
        try {
          activeRecorder.stop();
        } catch (error) {
          recording = false;
          reject(error);
        }
      });
    },

    isRecording(): boolean {
      return recording;
    },

    getMimeType(): string {
      return mimeType;
    },

    destroy(): void {
      if (recorder && recording) {
        try {
          recorder.stop();
        } catch {
          // Already stopped.
        }
      }
      if (audioRecorder && audioRecorder.state !== 'inactive') {
        try {
          audioRecorder.stop();
        } catch {
          // Already stopped.
        }
      }
      recorder = null;
      audioRecorder = null;
      recording = false;
      chunks = [];
      audioChunks = [];
      stopTracks();
      devLog.log('[videoRecorder] destroyed');
    },
  };
}
